import React, { useEffect, useMemo, useRef, useState } from "react";
import { Drawer } from "antd";

import {
  DEFAULT_RADIUS_KM,
  DEFAULT_SORT,
  DISTANCE_SORT,
  MAX_RADIUS_KM,
  MIN_RADIUS_KM,
  countActiveFilters,
  normaliseFilters,
  priceStep,
  sortOptionsFor,
} from "../accessoryFilters";
import { formatAmount } from "../accessoryMoney";

const Toggle = ({ label, checked, onChange }) => (
  <label className="sy-filter-toggle">
    <span>{label}</span>

    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />

    <i aria-hidden="true" />
  </label>
);

const Slider = ({ id, label, min, max, step, value, suffix, onChange }) => {
  const span = Math.max(1, max - min);
  const percent = Math.min(1, Math.max(0, (value - min) / span));

  return (
    <div className="home-slider" style={{ "--pct": percent }}>
      <div className="home-filter-head">
        <label htmlFor={id}>{label}</label>
        <output htmlFor={id}>{suffix(value)}</output>
      </div>

      <div className="home-slider-rail">
        <div className="home-slider-track" aria-hidden="true">
          <span className="home-slider-fill" />
        </div>

        <input
          id={id}
          className="home-slider-input"
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          aria-valuetext={suffix(value)}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      </div>
    </div>
  );
};

const PriceSection = ({ bounds, draft, update }) => {
  if (!bounds?.hasBounds) return null;

  const { minPrice: floor, maxPrice: ceiling, currency } = bounds;

  if (bounds.isFlat) {
    return (
      <div className="home-filter-group">
        <p className="home-filter-hint">
          {`Every matching accessory costs ${formatAmount(floor, currency)} — nothing to filter here.`}
        </p>
      </div>
    );
  }

  const step = priceStep(ceiling);
  const low = draft.minPrice ?? floor;
  const high = draft.maxPrice ?? ceiling;

  return (
    <fieldset className="home-filter-group">
      <legend>Price</legend>

      <Slider
        id="acc-filter-min-price"
        label="Minimum"
        min={floor}
        max={ceiling}
        step={step}
        value={low}
        suffix={(value) => formatAmount(value, currency)}
        onChange={(value) =>
          update({ minPrice: value, maxPrice: Math.max(value, high) })
        }
      />

      <Slider
        id="acc-filter-max-price"
        label="Maximum"
        min={floor}
        max={ceiling}
        step={step}
        value={high}
        suffix={(value) => formatAmount(value, currency)}
        onChange={(value) =>
          update({ maxPrice: value, minPrice: Math.min(value, low) })
        }
      />
    </fieldset>
  );
};

const AccessoryFiltersPanel = ({
  open,
  filters,
  materials,
  bounds,
  located,
  asSheet,
  onClose,
  onApply,
  onReset,
}) => {
  const [draft, setDraft] = useState(() => normaliseFilters(filters));
  const pendingRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    setDraft(normaliseFilters(filters));
  }, [open, filters]);

  useEffect(() => {
    if (!open) return;

    const { body } = document;
    const scrollY = window.scrollY;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";

    return () => {
      body.style.position = previous.position;
      body.style.top = previous.top;
      body.style.left = previous.left;
      body.style.right = previous.right;
      body.style.width = previous.width;
      body.style.overflow = previous.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const sortOptions = useMemo(() => sortOptionsFor(located), [located]);
  const activeCount = useMemo(() => countActiveFilters(draft), [draft]);

  const update = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const setRadius = (radiusKm) =>
    update({
      radiusKm,
      sort:
        radiusKm === null && draft.sort === DISTANCE_SORT ? DEFAULT_SORT : draft.sort,
    });

  const toggleMaterial = (id) =>
    update({
      materialIds: draft.materialIds.includes(id)
        ? draft.materialIds.filter((value) => value !== id)
        : [...draft.materialIds, id],
    });

  const tapApply = () => {
    pendingRef.current = { type: "apply", values: draft };
    onClose();
  };

  const tapReset = () => {
    pendingRef.current = { type: "reset" };
    onClose();
  };

  const onAfterClose = (isOpen) => {
    if (isOpen) return;

    const pending = pendingRef.current;
    pendingRef.current = null;

    if (!pending) return;
    if (pending.type === "reset") onReset();
    else onApply(pending.values);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      afterOpenChange={onAfterClose}
      placement={asSheet ? "bottom" : "right"}
      height={asSheet ? "86%" : undefined}
      width={asSheet ? undefined : "min(420px, 100vw)"}
      title={
        <header className="home-panel-header-card">
          <div className="home-panel-heading">
            <h2 className="home-panel-title">Filters</h2>
            <p className="home-panel-subtitle">
              {activeCount > 0
                ? `${activeCount} ${activeCount === 1 ? "filter" : "filters"} applied`
                : "Narrow down what you see"}
            </p>
          </div>

          <button
            type="button"
            className="home-panel-close"
            onClick={onClose}
            aria-label="Close filters"
          >
            <img
              src="/assets/icons/panel-close.svg"
              width="24"
              height="24"
              alt=""
              aria-hidden="true"
            />
          </button>
        </header>
      }
      closable={false}
      rootClassName="home-filters-drawer acc-filters-drawer"
      footer={
        <div className="home-filter-footer">
          <button
            type="button"
            className="home-filter-reset"
            disabled={countActiveFilters(filters) === 0}
            onClick={tapReset}
          >
            Clear All
          </button>

          <button type="button" className="home-filter-apply" onClick={tapApply}>
            Apply{activeCount > 0 ? ` (${activeCount})` : ""}
          </button>
        </div>
      }
    >
      <fieldset className="home-filter-group">
        <legend>Sort By</legend>

        <div className="home-filter-chips" role="radiogroup" aria-label="Sort by">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={draft.sort === option.value}
              className={`home-filter-chip${
                draft.sort === option.value ? " is-selected" : ""
              }`}
              onClick={() => update({ sort: option.value })}
            >
              {option.label}
            </button>
          ))}
        </div>
      </fieldset>

      {materials.length > 1 && (
        <fieldset className="home-filter-group">
          <legend>Choose Materials</legend>

          <div className="acc-checkboxes">
            {materials.map((material) => (
              <label key={material.id} className="acc-checkbox">
                <input
                  type="checkbox"
                  checked={draft.materialIds.includes(material.id)}
                  onChange={() => toggleMaterial(material.id)}
                />

                <span>{material.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <PriceSection bounds={bounds} draft={draft} update={update} />

      {located && (
        <fieldset className="home-filter-group">
          <legend>Radius</legend>

          <Slider
            id="acc-filter-radius"
            label="Search within"
            min={MIN_RADIUS_KM}
            max={MAX_RADIUS_KM}
            step={1}
            value={draft.radiusKm ?? MAX_RADIUS_KM}
            suffix={(value) => `${value} km`}
            onChange={setRadius}
          />

          <label className="acc-checkbox">
            <input
              type="checkbox"
              checked={draft.radiusKm === null}
              onChange={(event) =>
                setRadius(event.target.checked ? null : DEFAULT_RADIUS_KM)
              }
            />

            <span>Any distance</span>
          </label>
        </fieldset>
      )}

      <div className="home-filter-group">
        <Toggle
          label="In stock only"
          checked={draft.inStockOnly}
          onChange={(inStockOnly) => update({ inStockOnly })}
        />
      </div>
    </Drawer>
  );
};

export default React.memo(AccessoryFiltersPanel);
